// A17: org-level members + invites panel. Captain invites collaborators
// here; Waytide dispatches a magic-link email via Resend; invitees
// land at /invite/:token. Pending invites and accepted members both
// list here.

import { useEffect, useMemo, useState } from "react";
import {
  ApiError,
  createOrgInvite,
  extractRows,
  listOrgInvites,
} from "../api/client";

interface Props {
  orgId: number;
}

interface InviteRow {
  invite_id: number;
  email: string;
  role: string;
  accepted_at: number;
  created_at: number;
}

const ROLE_OPTIONS: Array<{ id: string; label: string; hint: string }> = [
  {
    id: "captain",
    label: "Captain",
    hint: "Can orchestrate workspaces, configure the org",
  },
  {
    id: "collaborator",
    label: "Collaborator",
    hint: "Can post in workspaces, run agent turns",
  },
  {
    id: "viewer",
    label: "Viewer",
    hint: "Read-only — sees workspaces, doesn't post",
  },
];

export function OrgMembersPanel({ orgId }: Props) {
  const [invites, setInvites] = useState<InviteRow[]>([]);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("collaborator");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    try {
      const result = await listOrgInvites(orgId);
      const raw = extractRows(result);
      const parsed: InviteRow[] = raw.map((r) => ({
        invite_id: Number(r[0] ?? 0),
        email: String(r[1] ?? ""),
        role: String(r[2] ?? ""),
        accepted_at: Number(r[3] ?? 0),
        created_at: Number(r[4] ?? 0),
      }));
      setInvites(parsed);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orgId]);

  const submit = async () => {
    if (!email.trim() || !email.includes("@")) return;
    setBusy(true);
    setError(null);
    try {
      await createOrgInvite(orgId, email.trim(), role);
      setEmail("");
      await refresh();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const pending = useMemo(
    () => invites.filter((i) => i.accepted_at === 0),
    [invites],
  );
  const accepted = useMemo(
    () => invites.filter((i) => i.accepted_at > 0),
    [invites],
  );

  return (
    <section
      className="flex flex-col gap-3 rounded-panel border border-edge bg-surface-glass p-4"
      data-testid="org-members-panel"
    >
      <header>
        <h3 className="text-sm font-semibold text-primary">
          Org members + invites
        </h3>
        <p className="mt-1 text-xs text-muted">
          Invitees get a magic-link email and land on a workspace list
          scoped to this org. Roles control what they can do.
        </p>
      </header>

      <div className="flex flex-col gap-2 rounded-md border border-edge bg-surface-raised p-2">
        <p className="text-xs font-medium text-primary">Invite someone</p>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@company.com"
            className="flex-1 rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary placeholder:text-faint"
          />
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="rounded-md border border-edge bg-surface px-2 py-1 text-xs text-primary"
            title={ROLE_OPTIONS.find((r) => r.id === role)?.hint ?? ""}
          >
            {ROLE_OPTIONS.map((r) => (
              <option key={r.id} value={r.id}>
                {r.label}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => void submit()}
            disabled={busy || !email.trim()}
            className="rounded-md border border-accent bg-accent/10 px-2 py-1 text-[0.625rem] font-medium text-accent hover:bg-accent/20 disabled:opacity-50"
          >
            {busy ? "Sending…" : "Send invite"}
          </button>
        </div>
      </div>

      {pending.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-[0.625rem] font-medium text-muted">
            Pending invites
          </p>
          {pending.map((i) => (
            <div
              key={i.invite_id}
              className="flex items-center justify-between gap-2 rounded-md border border-edge bg-surface-raised px-2 py-1"
            >
              <span className="text-xs text-primary">{i.email}</span>
              <span className="font-code text-[0.625rem] text-faint">
                {i.role} · sent {new Date(i.created_at / 1000).toLocaleString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {accepted.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="text-[0.625rem] font-medium text-muted">
            Accepted ({accepted.length})
          </p>
          {accepted.map((i) => (
            <div
              key={i.invite_id}
              className="flex items-center justify-between gap-2 rounded-md border border-edge bg-surface-raised px-2 py-1"
            >
              <span className="text-xs text-primary">{i.email}</span>
              <span className="font-code text-[0.625rem] text-success">
                {i.role} · joined
              </span>
            </div>
          ))}
        </div>
      )}

      {error && <p className="text-[0.625rem] text-danger">{error}</p>}
    </section>
  );
}
